import { useState } from "react";

const GOALS = [
  { id: "cut", label: "CUT", cal: -0.2, protein: 2.2 },
  { id: "maintain", label: "RECOMP", cal: 0, protein: 2.0 },
  { id: "bulk", label: "LEAN BULK", cal: 0.12, protein: 1.8 },
];

const ACTIVITY = [
  { id: "1.4", label: "Desk job, little movement outside training", factor: 1.4 },
  { id: "1.55", label: "Moderately active (walking, active job)", factor: 1.55 },
  { id: "1.7", label: "Very active (physical job / lots of steps)", factor: 1.7 },
];

export const CalorieCalculator = () => {
  const [form, setForm] = useState({ sex: "male", age: "", height: "", weight: "", activity: "1.55" });
  const [goal, setGoal] = useState("bulk");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const calculate = () => {
    const age = parseFloat(form.age);
    const height = parseFloat(form.height);
    const weight = parseFloat(form.weight);
    if (!age || !height || !weight || age < 10 || age > 100 || height < 100 || height > 250 || weight < 30 || weight > 300) {
      setResult(null);
      return;
    }
    // Mifflin-St Jeor BMR
    const bmr = 10 * weight + 6.25 * height - 5 * age + (form.sex === "male" ? 5 : -161);
    const factor = parseFloat(form.activity);
    const g = GOALS.find((x) => x.id === goal);
    const tdee = bmr * factor;
    const calories = Math.round(tdee * (1 + g.cal));
    const protein = Math.round(weight * g.protein);
    const fat = Math.round(weight * 0.9);
    const carbs = Math.max(0, Math.round((calories - protein * 4 - fat * 9) / 4));
    setResult({ calories, protein, fat, carbs, tdee: Math.round(tdee) });
  };

  return (
    <section id="calculator" className="py-24 sm:py-32">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <p className="section-label mb-4">// FUEL THE MACHINE //</p>
        <h2 className="font-anton text-4xl sm:text-5xl uppercase tracking-tight mb-6">
          Calorie <span className="red-text">Calculator</span>
        </h2>
        <p className="text-[#A1A1AA] mb-12 text-sm leading-relaxed">
          Built on the Mifflin-St Jeor equation and current hypertrophy research — calibrated for the 4-day
          Anime Physique split. Muscle isn't built in the gym, it's built at the table.
        </p>
        <div className="card-ady p-8 sm:p-10">
          <label className="font-jet text-xs tracking-[0.2em] text-[#A1A1AA] block mb-2">SEX</label>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {["male", "female"].map((s) => (
              <button
                key={s}
                data-testid={`calc-sex-${s}`}
                onClick={() => setForm({ ...form, sex: s })}
                className={`font-bebas tracking-[0.2em] py-3 border transition-colors ${
                  form.sex === s
                    ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
                    : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#39FF14]/50"
                }`}
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <label className="font-jet text-xs tracking-[0.2em] text-[#A1A1AA] block mb-2">AGE</label>
              <input data-testid="calc-age-input" type="number" className="input-ady" placeholder="25" value={form.age} onChange={set("age")} />
            </div>
            <div>
              <label className="font-jet text-xs tracking-[0.2em] text-[#A1A1AA] block mb-2">HEIGHT (CM)</label>
              <input data-testid="calc-height-input" type="number" className="input-ady" placeholder="180" value={form.height} onChange={set("height")} />
            </div>
            <div>
              <label className="font-jet text-xs tracking-[0.2em] text-[#A1A1AA] block mb-2">WEIGHT (KG)</label>
              <input data-testid="calc-weight-input" type="number" className="input-ady" placeholder="75" value={form.weight} onChange={set("weight")} />
            </div>
          </div>
          <label className="font-jet text-xs tracking-[0.2em] text-[#A1A1AA] block mb-2">ACTIVITY OUTSIDE TRAINING</label>
          <select data-testid="calc-activity-select" className="input-ady mb-6" value={form.activity} onChange={set("activity")}>
            {ACTIVITY.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
          <label className="font-jet text-xs tracking-[0.2em] text-[#A1A1AA] block mb-2">GOAL</label>
          <div className="grid grid-cols-3 gap-3 mb-8">
            {GOALS.map((g) => (
              <button
                key={g.id}
                data-testid={`calc-goal-${g.id}`}
                onClick={() => setGoal(g.id)}
                className={`font-bebas tracking-[0.2em] py-3 border transition-colors ${
                  goal === g.id
                    ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
                    : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#39FF14]/50"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <button data-testid="calc-submit-btn" onClick={calculate} className="btn-brutal btn-brutal-green w-full">
            Calculate
          </button>
          {result !== null && (
            <div data-testid="calc-result" className="mt-8 border border-dashed border-[#39FF14] p-6">
              <div className="text-center mb-6">
                <p className="font-jet text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-2">DAILY CALORIE TARGET</p>
                <p className="font-anton text-5xl neon-text" data-testid="calc-calories">
                  {result.calories}
                  <span className="text-2xl text-[#F5F5F5] ml-2">kcal</span>
                </p>
                <p className="font-jet text-[10px] text-[#52525B] mt-2">MAINTENANCE (TDEE): {result.tdee} KCAL</p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="border border-[#2A2A2A] p-4">
                  <p className="font-anton text-2xl text-[#39FF14]" data-testid="calc-protein">{result.protein}g</p>
                  <p className="font-jet text-[10px] tracking-[0.2em] text-[#A1A1AA] mt-1">PROTEIN</p>
                </div>
                <div className="border border-[#2A2A2A] p-4">
                  <p className="font-anton text-2xl text-[#D90429]">{result.fat}g</p>
                  <p className="font-jet text-[10px] tracking-[0.2em] text-[#A1A1AA] mt-1">FAT</p>
                </div>
                <div className="border border-[#2A2A2A] p-4">
                  <p className="font-anton text-2xl text-[#F5F5F5]">{result.carbs}g</p>
                  <p className="font-jet text-[10px] tracking-[0.2em] text-[#A1A1AA] mt-1">CARBS</p>
                </div>
              </div>
              <p className="font-jet text-[10px] text-[#52525B] mt-5 leading-relaxed">
                METHOD: MIFFLIN-ST JEOR BMR × ACTIVITY (INCL. 4 TRAINING DAYS) • PROTEIN 1.6–2.2 G/KG (MORTON ET AL. 2018) •
                LEAN BULK +10–15% / CUT −20% (IRAKI ET AL. 2019) • FAT ≥0.9 G/KG FOR HORMONAL HEALTH • REST = CARBS TO FUEL THE SPLIT
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
